import React, { ChangeEvent, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button, Checkbox } from 'antd';
// Constants
import { MAX_FILES, MAX_IMAGE_SIZE, MAX_VIDEO_SIZE } from '../../constants';
// Helpers
import { UploadFileType } from 'helpers/campaign';
import { getContentTypeText } from 'helpers/socialMediaContentTypes';
import {
  isSupportedVideoFormat,
  isSupportedVideoExtension,
} from 'helpers/videoFormats';
// Types
import { CampaignTaskType } from 'api/graphql-global-types';
import { TaskData } from '../../CampaignDeliverablesSubmitForReviewForm';
// UiShared
import TextArea from 'uiShared/Textarea/Textarea';
// UI
import { errorNotification } from 'ui/Notification';
// Styles
import styles from './SubmitV2.module.scss';

type TaskDropzoneProps = {
  taskId: string;
  onDrop: (files: File[], taskId: string) => void;
  currentTaskData: TaskData;
};

const TaskDropzone = ({
  taskId,
  onDrop,
  currentTaskData,
}: TaskDropzoneProps) => {
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (files) => onDrop(files, taskId),
    maxFiles: MAX_FILES,
    multiple: true,
  });

  return (
    <div className={styles.dropzoneWrapper} {...getRootProps()}>
      <input {...getInputProps()} />
      <p className={styles.title}>Drag & drop or click to select files.</p>
      <p className={styles.subtitle}>
        Upload JPEG and PNG images or video files in supported formats (MP4,
        MOV, AVI, WebM, etc.).
      </p>
      <div className={styles.previewsContainer}>
        {currentTaskData.files.map((file, fileIndex) => {
          const isImage = file.path?.match(/\.(jpeg|jpg|png)$/i);
          const fileExtension =
            file.path?.split('.').pop()?.toLowerCase() || '';
          const isVideo =
            fileExtension && isSupportedVideoExtension(fileExtension);

          return (
            <div key={fileIndex} className={styles.previewWrapper}>
              <p className={styles.fileName}>{file.name}</p>
              {isImage ? (
                <img
                  src={file.preview}
                  alt={file.name}
                  className={styles.preview}
                />
              ) : isVideo ? (
                <video
                  src={file.preview}
                  controls
                  className={styles.preview}
                  crossOrigin="anonymous"
                >
                  <track
                    kind="captions"
                    label="English captions"
                    srcLang="en"
                    src=""
                    default
                  />
                  <source src={file.preview} type={file.type} />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <p>Unsupported file type</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

type SubmitV2Props = {
  tasks: any[];
  taskData: Record<string, TaskData>;
  onTaskDataChange: (taskId: string, data: TaskData) => void;
  isLoading: boolean;
};

const SubmitV2 = ({
  tasks,
  taskData,
  onTaskDataChange,
  isLoading,
}: SubmitV2Props): JSX.Element => {
  const onDrop = useCallback(
    (acceptedFiles: File[], taskId: string) => {
      const validFiles = acceptedFiles.filter((file) => {
        const isVideo = isSupportedVideoFormat(file.type);
        const isImage = file.type === 'image/jpeg' || file.type === 'image/png';
        const isValidSize = isVideo
          ? file.size <= MAX_VIDEO_SIZE
          : file.size <= MAX_IMAGE_SIZE;
        return (isVideo || isImage) && isValidSize;
      });

      const filesWithPreview: UploadFileType[] = validFiles.map((file) => {
        return Object.assign(file, {
          preview: URL.createObjectURL(file),
          fileName: file.name,
        });
      });

      const currentTaskData = taskData[taskId] || {
        postCopy: '',
        files: [],
        checked: false,
      };
      const totalFiles = currentTaskData.files.length + filesWithPreview.length;

      if (totalFiles > MAX_FILES) {
        errorNotification(
          `Please upload up to ${MAX_FILES} files. You are trying to upload ${totalFiles}!`
        );
      } else {
        onTaskDataChange(taskId, {
          ...currentTaskData,
          files: [...currentTaskData.files, ...filesWithPreview],
        });
      }
    },
    [taskData, onTaskDataChange]
  );

  const handlePostCopyChange = (
    taskId: string,
    newValue: ChangeEvent<HTMLTextAreaElement>
  ) => {
    const currentTaskData = taskData[taskId] || {
      postCopy: '',
      files: [],
      checked: false,
    };
    onTaskDataChange(taskId, {
      ...currentTaskData,
      postCopy: newValue.target.value,
    });
  };

  const handleCheckboxChange = (taskId: string, checked: boolean) => {
    const currentTaskData = taskData[taskId] || {
      postCopy: '',
      files: [],
      checked: false,
    };
    onTaskDataChange(taskId, {
      ...currentTaskData,
      checked,
    });
  };

  const handleReset = (taskId: string) => {
    const currentTaskData = taskData[taskId] || {
      postCopy: '',
      files: [],
      checked: false,
    };
    onTaskDataChange(taskId, {
      ...currentTaskData,
      files: [],
    });
  };

  return (
    <div className={styles.v2Container}>
      <h3 className={styles.v2Title}>Deliverables & Tasks</h3>
      {tasks?.map((task, index) => {
        const isContent = task.type === CampaignTaskType.CONTENT;
        const currentTaskData = taskData[task.id] || {
          postCopy: '',
          files: [],
          checked: false,
        };
        const showClear = Boolean(currentTaskData.files.length);

        // Don't show IG Story deliverable, no copy needed for it
        if (task.type === CampaignTaskType.IG_STORY) {
          return null;
        }

        return (
          <div key={task.id} className={styles.taskSection}>
            <div className={styles.taskHeader}>
              <div className={styles.taskTitleRow}>
                <Checkbox
                  checked={currentTaskData.checked}
                  onChange={(e) =>
                    handleCheckboxChange(task.id, e.target.checked)
                  }
                  className={styles.taskCheckbox}
                />
                <p className={styles.taskNumber}>
                  Deliverable {index + 1} - {task.title}
                </p>
              </div>
              <p className={styles.taskType}>
                Type: {getContentTypeText(task)}
              </p>
            </div>
            <div className={styles.taskDescription}>
              <p className={styles.descriptionText}>
                Description: {task.description}
              </p>
            </div>

            {!isContent && (
              <div className={styles.inputWrapper}>
                <p className={styles.label}>Post Copy</p>
                <TextArea
                  placeholder="Please write the copy you'd like to use for this deliverable."
                  name={`postCopy-${task.id}`}
                  onChange={(e) => handlePostCopyChange(task.id, e)}
                  className={styles.textarea}
                  rows={3}
                  value={currentTaskData.postCopy}
                />
              </div>
            )}

            {isContent && (
              <>
                {showClear && (
                  <Button
                    loading={isLoading}
                    onClick={() => handleReset(task.id)}
                    style={{ marginBottom: '16px' }}
                  >
                    Clear Files
                  </Button>
                )}

                <TaskDropzone
                  taskId={task.id}
                  onDrop={onDrop}
                  currentTaskData={currentTaskData}
                />
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SubmitV2;
